import { useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import type { Location } from 'history';
import { ZodType } from 'zod';

import { objectEntries } from '@/utils/ts/object-entries';

type SelectedFields<F extends {}> = {
  [K in keyof F]: SelectedField<F[K]>;
};

type SelectedField<T> = {
  key: string;
  schema: ZodType<T>;
  codec: Codec<T>;
  transform?: (value: T) => T;
};

type Codec<T> = {
  encode: (data: T) => string;
  decode: (encoded: string) => any;
};

type Updator<F extends {}> = Partial<F> | ((existingFields: F) => Partial<F>);

function resolveUpdator<F extends {}>(
  updator: Updator<F>,
  existingFields: F,
): Partial<F> {
  if (typeof updator === 'function') {
    return updator(existingFields);
  } else {
    return updator;
  }
}

export type PushAndReplace<F extends {}> = {
  replace: (updator: Updator<F>) => void;
  push: (updator: Updator<F>) => void;
};

export type UseQueryParamsResult<F extends {}> = [F, PushAndReplace<F>];

function fieldsFromSelectedFields<F extends {}>(
  selectedFields: SelectedFields<F>,
  location: Location,
): F {
  const searchParams = new URLSearchParams(location.search);

  const fields: F = {} as F;

  for (const [propName, selectedField] of objectEntries(selectedFields)) {
    const rawValue = searchParams.get(selectedField.key);
    const decodedValue = rawValue
      ? selectedField.codec.decode(rawValue)
      : undefined;

    if (selectedField.schema.isOptional()) {
      const result = selectedField.schema.safeParse(decodedValue);

      if (result.success && typeof result.data !== 'undefined') {
        const value = selectedField.transform
          ? selectedField.transform(result.data)
          : result.data;
        fields[propName] = value;
      }

      continue;
    }

    const value = selectedField.schema.parse(decodedValue);

    if (typeof value !== 'undefined') {
      const transformedValue = selectedField.transform
        ? selectedField.transform(value)
        : value;
      fields[propName] = transformedValue;
    }
  }

  return fields;
}

export function useQueryParams<F extends {}>(
  selectedFields: SelectedFields<F>,
): UseQueryParamsResult<F> {
  const location = useLocation();
  const history = useHistory();

  const fields = useMemo<F>(
    () => fieldsFromSelectedFields(selectedFields, location),
    [selectedFields, location],
  );

  const update = useMemo<PushAndReplace<F>>(
    function () {
      function makeUpdateField(pushOrReplace: (l: Partial<Location>) => void) {
        return function updatedFields(updator: Updator<F>) {
          const updatedFields = resolveUpdator(updator, fields);
          const searchParams = new URLSearchParams(history.location.search);

          for (const [prop, value] of objectEntries(updatedFields)) {
            const selectedField = selectedFields[prop];

            if (!selectedField) {
              console.warn(
                `You tried to set the query param ${String(prop)} with the value ${value}, but it is not defined in the selectedFields`,
              );
              continue;
            }

            if (typeof value === 'undefined') {
              searchParams.delete(selectedField.key);
              continue;
            }

            const encodedValue = selectedField.codec.encode(value);
            searchParams.set(selectedField.key, encodedValue);
          }

          pushOrReplace({ search: searchParams.toString() });
        };
      }

      return {
        push: makeUpdateField(history.push),
        replace: makeUpdateField(history.replace),
      };
    },
    [fields, selectedFields, history],
  );

  return [fields, update];
}

export namespace codec {
  export const number: Codec<number | undefined> = {
    encode: (num) => (typeof num !== 'undefined' ? String(num) : ''),
    decode: (str) => (str ? Number(str) : undefined),
  };

  export const string: Codec<string | undefined> = {
    encode: (n) => n ?? '',
    decode: (n) => {
      if (!n) return undefined;
      // Decode URL-encoded characters and remove surrounding quotes
      const decoded = decodeURIComponent(n);
      // Remove surrounding quotes if they exist
      return decoded.replace(/^["']|["']$/g, '');
    },
  };

  export const date: Codec<Date | undefined> = {
    encode: (date) => (date ? format(date, 'yyyy-MM-dd') : ''),
    decode(str) {
      try {
        return new Date(str);
      } catch (_) {
        return undefined;
      }
    },
  };

  export const datetime: Codec<Date | undefined> = {
    encode: (date) => (date ? format(date, "yyyy-MM-dd'T'HH:mm:ss") : ''),
    decode(str) {
      try {
        return new Date(str);
      } catch (_) {
        return undefined;
      }
    },
  };

  export const boolean: Codec<boolean | undefined> = {
    encode: (bool) => (typeof bool === 'undefined' ? '' : bool ? '1' : '0'),
    decode(str) {
      switch (str) {
        case '1': {
          return true;
        }
        case '0': {
          return false;
        }
        default: {
          return undefined;
        }
      }
    },
  };

  export const array = <T>(
    as: Codec<T>,
    separator: string = ',',
  ): Codec<T[] | undefined> => ({
    encode: (data) =>
      data?.map((item) => as.encode(item)).join(separator) ?? '',
    decode: (str) => {
      if (!str) return undefined;
      // Split by separator and decode each item
      return str.split(separator).map((item) => as.decode(item.trim()));
    },
  });

  export const tuple = <T>(
    codecs: Codec<T>[],
    separator: string = '|',
  ): Codec<T[] | undefined> => ({
    encode(data) {
      return (
        codecs
          .map((codec, index) =>
            data?.[index] ? codec.encode(data[index]) : '',
          )
          .join(separator) ?? ''
      );
    },
    decode(str) {
      const values = str.split(separator);
      return codecs.map((codec, index) => codec.decode(values[index]));
    },
  });
}
